/**
 * BienvenidaSOA10 — popup de primer ingreso (brand book 6 · §15e).
 * 4 pasos: cambio de nombre · lo nuevo según el rol · la app en el celular · listo y por dónde empezar.
 * Escritorio: modal de 960 × 620. Menos de 768 px: hoja inferior desde 90 px del borde superior, se avanza deslizando.
 * Una vez por persona (preferencia en el backend, no por dispositivo). «Saltar» cuenta como vista.
 * En el modo prueba del candado se muestra una vez por sesión y NO se guarda, para que la vean de verdad al abrir.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "../lib/icons.jsx";
import { useAuth } from "../context/AuthContext.jsx";
import { useUserPref } from "../hooks/useUserPref.js";
import { api } from "../api/client.js";
import { activarPush } from "../lib/pushStaff.js";
import AvatarPicker from "./AvatarPicker.jsx";
import { useFlag } from "../lib/useFlags.js";

export const EVENTO_ABRIR_BIENVENIDA = "crew-abrir-bienvenida";
const CLAVE_PREF = "bienvenida_soa10pro";
const CLAVE_SESION = "crew_bienvenida_prueba_vista";

// Familias de color de las fichas (mismas del sistema).
const FAM = { c: ["#CFFAFE", "#155E75"], v: ["#EDE9FE", "#5B21B6"], g: ["#D1FAE5", "#065F46"], a: ["#FEF3C7", "#92400E"], r: ["#FEE2E2", "#B91C1C"] };
const f = (ic, k, t, d) => ({ ic, k, t, d });

export const BIENVENIDA_POR_ROL = {
  EC: { n: "ejecutivas de cuenta", corto: "EC", cta: "Empezar por Triage", ruta: "/triage", f: [
    f("inbox", "c", "Triage", "Pedidos del portal, agentes y calidad en una sola cola."),
    f("donut_large", "g", "Fee en vivo", "Los créditos del cliente a la vista al crear cada tarea."),
    f("photo_camera", "v", "Cobertura Pro", "7 fases, crew que confirma por Calendar y crudos que avisa Drive."),
  ] },
  COM: { n: "community managers", corto: "COM", cta: "Empezar por Planes", ruta: "/generador-contenido", f: [
    f("auto_awesome", "v", "Planes", "Una sola entrada: brief, generar y el Doc listo para editar."),
    f("inbox", "c", "Triage", "Comentarios de clientes y QC en una sola cola."),
    f("keyboard_command_key", "a", "⌘K que crea", "Escribí «reel bernardo viernes» y la tarea sale armada."),
  ] },
  CCC: { n: "coordinación", corto: "coordinación", cta: "Empezar por Coberturas", ruta: "/coberturas-v2", f: [
    f("photo_camera", "v", "Tablero de coberturas", "Próximos 14 días ordenados por lo que falta para salir."),
    f("person_search", "c", "Asignación por carga", "Cada tarea al especialista con menos carga del rol."),
    f("inbox", "a", "Triage", "Lo que espera una decisión tuya, en un solo lugar."),
  ] },
  Creativo: { n: "equipo creativo", corto: "creativos", cta: "Empezar por Mi Día", ruta: "/inicio", f: [
    f("today", "c", "Mi Día renovado", "Tus tareas de hoy con horas y prioridad."),
    f("install_mobile", "g", "La app en tu celular", "Mi Día, Triage y la cobertura de hoy, a un toque."),
    f("checklist", "v", "Shot list en vivo", "Se marca en el celular durante la cobertura."),
  ] },
  CFO: { n: "finanzas", corto: "finanzas", cta: "Empezar por Fee", ruta: "/fee-ledger", f: [
    f("donut_large", "g", "Fee en vivo", "Consumo de cada cliente y excesos avisados antes de producir."),
    f("memory", "a", "Motores de IA", "Gasto por función y cambio de modelo sin tocar código."),
    f("payments", "r", "Cobranzas", "Recordatorios según cómo paga cada cliente."),
  ] },
};

const SIGUE_IGUAL = ["Tu usuario y tu clave siguen igual", "Tus tareas, clientes y datos, intactos", "Los mismos atajos de teclado y rutas"];
const AYUDA = [
  { ic: "keyboard_command_key", t: "⌘K para buscar cualquier pantalla o crear una tarea" },
  { ic: "help", t: "Ayuda › Ver bienvenida, para repetir esto" },
  { ic: "forum", t: "El espacio «SOA 10 PRO» en Chat para preguntas" },
];

const tile = (k, s = 44) => ({
  width: s, height: s, borderRadius: Math.round(s * 0.31), flexShrink: 0, background: FAM[k][0], color: FAM[k][1],
  display: "flex", alignItems: "center", justifyContent: "center",
});

function useEsMovil() {
  const [m, setM] = useState(typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const on = () => setM(mq.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return m;
}

const soloDigitos = (v) => String(v || "").replace(/\D/g, "");
const whatsappValido = (v) => soloDigitos(v).length >= 9;
// Cumpleaños: fecha real, no futura y de alguien de 14 a 90 años.
const cumpleValido = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v || ""));
  if (!m) return false;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (isNaN(d) || d.getMonth() !== Number(m[2]) - 1) return false;
  const edad = (Date.now() - d.getTime()) / (365.25 * 24 * 3600 * 1000);
  return edad >= 14 && edad <= 90;
};

// Cumpleaños cargados antes en texto libre («October 28», «28 de octubre»): sirven para festejar, no se vuelven a pedir.
const cumpleLegado = (v) => { const t = String(v || "").trim(); return t.length >= 4 && /[A-Za-zÁ-ú]/.test(t) && /\d/.test(t) && !/^\d{4}-\d{2}-\d{2}/.test(t); };

// Datos de perfil que se exigen en el ingreso: foto, WhatsApp y cumpleaños.
function usePerfil(miembro) {
  const [foto, setFoto] = useState(miembro?.foto_url || "");
  const [whatsapp, setWhatsapp] = useState(miembro?.whatsapp || "");
  const [cumple, setCumple] = useState(/^\d{4}-\d{2}-\d{2}/.test(String(miembro?.cumpleanos || "")) ? String(miembro.cumpleanos).slice(0, 10) : "");
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const [hecho, setHecho] = useState(false);
  // El miembro llega despues del primer render: se sincronizan los valores cuando aparece.
  useEffect(() => {
    if (!miembro?.id) return;
    setFoto(miembro.foto_url || "");
    setWhatsapp(miembro.whatsapp || "");
    setCumple(/^\d{4}-\d{2}-\d{2}/.test(String(miembro.cumpleanos || "")) ? String(miembro.cumpleanos).slice(0, 10) : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [miembro?.id]);
  const legado = cumpleLegado(miembro?.cumpleanos) ? String(miembro.cumpleanos).trim() : "";
  const completo = !!foto && whatsappValido(whatsapp) && (cumpleValido(cumple) || (!cumple && !!legado));
  const guardar = useCallback(async () => {
    if (!miembro?.id) return false;
    setGuardando(true); setError(null);
    try {
      await api.request("PATCH", `/api/equipo/${miembro.id}`, { whatsapp: whatsapp.trim(), ...(cumple ? { cumpleanos: cumple } : {}) });
      setHecho(true);
      return true;
    } catch (e) { setError(e?.message || "No se pudo guardar"); return false; }
    finally { setGuardando(false); }
  }, [miembro?.id, whatsapp, cumple]);
  // Si el perfil ya estaba completo al entrar (o se completó en esta sesión), no se vuelve a pedir.
  const yaCompleto = hecho || (!!miembro?.foto_url && whatsappValido(miembro?.whatsapp) && (cumpleValido(String(miembro?.cumpleanos || "").slice(0, 10)) || !!legado));
  return { foto, setFoto, whatsapp, setWhatsapp, cumple, setCumple, legado, guardando, error, completo, yaCompleto, guardar };
}

const inputPerfil = { width: "100%", boxSizing: "border-box", height: 52, borderRadius: 16, border: "2px solid #CDD0D7", padding: "0 16px", fontSize: 17, fontFamily: "inherit", color: "#111318", background: "#FFFFFF", outline: "none" };

function CamposPerfil({ perfil }) {
  const okWa = whatsappValido(perfil.whatsapp);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 14, fontWeight: 800 }}>
        WhatsApp <span style={{ color: "#B91C1C" }}>*</span>
        <input type="tel" inputMode="tel" autoComplete="tel" value={perfil.whatsapp} onChange={e => perfil.setWhatsapp(e.target.value)}
          placeholder="+595 981 123 456" aria-invalid={perfil.whatsapp && !okWa} style={{ ...inputPerfil, borderColor: perfil.whatsapp && !okWa ? "#DC2626" : "#CDD0D7" }} />
        {perfil.whatsapp && !okWa && <span style={{ fontSize: 13, fontWeight: 600, color: "#B91C1C" }}>Escribí el número completo, con código de país.</span>}
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 14, fontWeight: 800 }}>
        Cumpleaños <span style={{ color: "#B91C1C" }}>*</span> <span style={{ fontWeight: 600, color: "#6B6F7A" }}>(para festejarte con el equipo)</span>
        <input type="date" value={perfil.cumple} max={new Date().toISOString().slice(0, 10)} onChange={e => perfil.setCumple(e.target.value)}
          aria-invalid={!!perfil.cumple && !cumpleValido(perfil.cumple)} style={{ ...inputPerfil, borderColor: perfil.cumple && !cumpleValido(perfil.cumple) ? "#DC2626" : "#CDD0D7" }} />
        {perfil.cumple && !cumpleValido(perfil.cumple) && <span style={{ fontSize: 13, fontWeight: 600, color: "#B91C1C" }}>Revisá la fecha.</span>}
        {!perfil.cumple && perfil.legado && <span style={{ fontSize: 13, fontWeight: 600, color: "#065F46" }}>Ya tenemos tu cumpleaños: {perfil.legado}. Podés dejarlo así.</span>}
      </label>
      {perfil.error && <span role="alert" style={{ fontSize: 14, fontWeight: 700, color: "#B91C1C" }}>{perfil.error}</span>}
    </div>
  );
}

function BloqueFoto({ perfil, nombre, tam = 140 }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
      <AvatarPicker currentUrl={perfil.foto} nombre={nombre} size={tam} onUploaded={(u) => perfil.setFoto(u)} />
      <span style={{ fontSize: 14, fontWeight: 800, color: perfil.foto ? "#065F46" : "#B91C1C" }}>
        {perfil.foto ? "Foto lista" : "La foto es obligatoria *"}
      </span>
    </div>
  );
}

function grupoDeRol(a) {
  if (a.isCOO || a.isCCC || a.isCPC) return "CCC";
  if (a.isCuenta) return "EC";
  if (a.isCM) return "COM";
  if (a.isEspecialista) return "Creativo";
  if (a.isCFO || a.isAdmin) return "CFO";
  return "EC";   // rol desconocido → contenido de EC
}

export default function BienvenidaSOA10({ guardar = true }) {
  const auth = useAuth();
  const { miembro } = auth;
  const navigate = useNavigate();
  const movil = useEsMovil();
  const triageOn = useFlag("ff_triage");
  const [pref, setPref, { synced } = {}] = useUserPref(CLAVE_PREF, null);
  const [abierta, setAbierta] = useState(false);
  const [paso, setPaso] = useState(0);
  const [envio, setEnvio] = useState(null);   // null | "enviando" | "ok" | "error"
  const perfil = usePerfil(miembro);
  const [visto, setVisto] = useState(false);
  const instalarRef = useRef(null);
  const toqueRef = useRef(null);
  const dialogRef = useRef(null);

  const grupo = grupoDeRol(auth);
  const R0 = BIENVENIDA_POR_ROL[grupo] || BIENVENIDA_POR_ROL.EC;
  // Triage todavia puede estar apagado: el boton lleva a Mi Dia hasta que se encienda.
  const R = (R0.ruta === "/triage" && !triageOn) ? { ...R0, ruta: "/inicio", cta: "Empezar por Mi Día" } : R0;
  const nombre = (miembro?.nombre || "").split(" ")[0] || "";
  const esEquipo = String(miembro?.email || auth.user?.email || "").toLowerCase().endsWith("@thecrewagencia.com");

  // Decide si se abre sola: una vez por persona, solo equipo, no en links directos de trabajo urgente.
  useEffect(() => {
    if (!miembro || !esEquipo || (synced === false)) return;
    if (pref && pref.at) return;
    const ruta = window.location.pathname + window.location.search;
    if (/^\/triage/.test(ruta) || /[?&]focus=/.test(ruta) || /^\/tareas\/[^/]+/.test(ruta)) return;
    if (!guardar) { try { if (sessionStorage.getItem(CLAVE_SESION)) return; } catch { /* sin storage */ } }
    setAbierta(true);
  }, [miembro, esEquipo, synced, pref, guardar]);

  // «Ayuda › Ver bienvenida»: la abre de nuevo aunque ya esté vista.
  useEffect(() => {
    const abrir = () => { setPaso(0); setEnvio(null); setAbierta(true); };
    window.addEventListener(EVENTO_ABRIR_BIENVENIDA, abrir);
    return () => window.removeEventListener(EVENTO_ABRIR_BIENVENIDA, abrir);
  }, []);

  // El celular dispara beforeinstallprompt: se guarda para el botón «Agregar a inicio».
  useEffect(() => {
    const on = (e) => { e.preventDefault(); instalarRef.current = e; };
    window.addEventListener("beforeinstallprompt", on);
    return () => window.removeEventListener("beforeinstallprompt", on);
  }, []);

  // Escritorio, paso 3: manda el link de instalación por mail (una vez por apertura).
  useEffect(() => {
    if (!abierta || movil || paso !== 3 || envio) return;
    setEnvio("enviando");
    api.enviarLinkApp().then(() => setEnvio("ok")).catch(() => setEnvio("error"));
  }, [abierta, movil, paso, envio]);

  const cerrar = useCallback((modo, ir) => {
    setAbierta(false);
    setVisto(true);
    if (guardar) setPref({ at: new Date().toISOString(), modo, paso });
    else { try { sessionStorage.setItem(CLAVE_SESION, "1"); } catch { /* sin storage */ } }
    if (ir) navigate(ir);
  }, [guardar, setPref, navigate, paso]);

  const siguiente = useCallback(async () => {
    if (paso === 1) {
      if (!perfil.completo) return;
      if (!(await perfil.guardar())) return;
    }
    if (paso < 4) {
      if (movil && paso === 3) {
        try { if (instalarRef.current) { await instalarRef.current.prompt(); instalarRef.current = null; } } catch { /* cancelado */ }
        try { await activarPush(); } catch { /* sin permiso: no bloquea */ }
      }
      setPaso(paso + 1);
    } else cerrar("cta", R.ruta);
  }, [paso, movil, cerrar, R.ruta, perfil]);
  const atras = useCallback(() => setPaso(p => Math.max(0, p - 1)), []);

  // Teclado: ← → navegan, Esc salta; el foco queda dentro del diálogo.
  useEffect(() => {
    if (!abierta) return;
    const previo = document.activeElement;
    dialogRef.current?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); cerrar("saltar"); }
      else if ((e.key === "ArrowRight" || e.key === "ArrowLeft") && /^(INPUT|TEXTAREA)$/.test(e.target?.tagName || "")) return;   // al escribir, las flechas mueven el cursor
      else if (e.key === "ArrowRight") siguiente();
      else if (e.key === "ArrowLeft") atras();
      else if (e.key === "Tab" && dialogRef.current) {
        const foc = [...dialogRef.current.querySelectorAll("button, [href], [tabindex]:not([tabindex='-1'])")].filter(x => !x.disabled);
        if (!foc.length) return;
        const primero = foc[0], ultimo = foc[foc.length - 1];
        if (e.shiftKey && document.activeElement === primero) { e.preventDefault(); ultimo.focus(); }
        else if (!e.shiftKey && document.activeElement === ultimo) { e.preventDefault(); primero.focus(); }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("keydown", onKey); try { previo?.focus?.(); } catch { /* sin foco */ } };
  }, [abierta, cerrar, siguiente, atras]);

  const pasos = useMemo(() => [
    { t: `Hola, ${nombre}. The Crew OS ahora es SOA 10 PRO.`, d: "Cambiamos el nombre, el ícono y la piel. Por dentro es el mismo sistema que ya usás, con mejoras.", tm: `Hola, ${nombre}. Ahora es SOA 10 PRO.`, dm: "Mismo sistema, nombre y piel nuevos." },
    { t: "Completá tu perfil", d: "Tu foto aparece en tareas, chats y menciones, el WhatsApp es para que el equipo te encuentre rápido y el cumpleaños para festejarte.", tm: "Completá tu perfil", dm: "Foto, WhatsApp y cumpleaños, para que el equipo te reconozca." },
    { t: `Lo nuevo para ${R.corto}`, d: "Tres cosas que cambian tu día a día desde hoy.", tm: `Lo nuevo para ${R.corto}`, dm: "Tres cosas que cambian tu día." },
    { t: "Llevalo en el celular", d: "SOA 10 PRO se instala como una app. Te avisa solo cuando algo necesita una decisión tuya.", tm: "Instalalo en tu celular", dm: "Te avisa solo cuando necesita una decisión tuya." },
    { t: "Listo. Arranquemos.", d: "Si te perdés, tenés tres caminos a mano.", tm: "Listo. Arranquemos.", dm: "Tres caminos si te perdés." },
  ], [nombre, R.corto]);

  const enMiPerfil = typeof window !== "undefined" && /^\/mi-perfil/.test(window.location.pathname);
  const yaVista = visto || !!(pref && pref.at);
  const bloquear = !abierta && !!miembro && esEquipo && yaVista && !perfil.yaCompleto && !perfil.completo && !enMiPerfil;
  if (bloquear) {
    return (
      <div style={{ ...{ position: "fixed", inset: 0, zIndex: 9995, background: "rgba(11,13,16,0.55)" }, display: "grid", placeItems: "center", padding: 16 }}>
        <div role="dialog" aria-modal="true" aria-labelledby="perfil-titulo" style={{ width: "min(520px, 100%)", maxHeight: "calc(100vh - 32px)", overflowY: "auto", background: "#FFFFFF", borderRadius: 32, padding: "32px 32px 28px", display: "flex", flexDirection: "column", gap: 18, boxShadow: "0 50px 120px rgba(0,0,0,0.4)" }}>
          <span style={{ fontSize: 14, fontWeight: 800, color: "#0E7490", textTransform: "uppercase", letterSpacing: "0.06em" }}>Falta un paso</span>
          <h2 id="perfil-titulo" style={{ margin: 0, fontSize: 30, lineHeight: 1.1, fontWeight: 900, letterSpacing: "-0.02em" }}>Completá tu perfil para seguir</h2>
          <p style={{ margin: 0, fontSize: 16, lineHeight: 1.5, color: "#3B3E46" }}>Tu foto aparece en tareas, chats y menciones. Son tres datos y listo.</p>
          <BloqueFoto perfil={perfil} nombre={miembro.nombre} tam={112} />
          <CamposPerfil perfil={perfil} />
          <button type="button" disabled={!perfil.completo || perfil.guardando} onClick={() => perfil.guardar()}
            style={{ height: 54, borderRadius: 16, border: 0, background: "#111318", color: "#FFFFFF", fontSize: 16, fontWeight: 800, cursor: "pointer", opacity: perfil.completo ? 1 : 0.4 }}>
            {perfil.guardando ? "Guardando…" : "Guardar y entrar"}
          </button>
        </div>
      </div>
    );
  }
  if (!abierta || !miembro) return null;

  const P = pasos[paso];
  const ctaTxt = paso === 4 ? R.cta : "Siguiente";
  const ctaMovil = paso === 3 ? "Agregar a inicio" : ctaTxt;
  const textoEnvio = envio === "ok" ? "Te mandamos el link por mail ahora" : envio === "error" ? "No pudimos mandar el mail: abrí os.thecrewagencia.com desde tu celular" : "Te mandamos el link por mail ahora";
  const appPasos = [textoEnvio, "Abrilo en el celular y tocá «Agregar a inicio»", "Permití los avisos para tus decisiones"];
  const movilItems = paso === 0 ? SIGUE_IGUAL.map(t => ({ ic: ["person", "database", "keyboard"][SIGUE_IGUAL.indexOf(t)], t, k: "g" }))
    : paso === 1 ? []
    : paso === 2 ? R.f.map(x => ({ ic: x.ic, t: x.t, k: x.k }))
    : paso === 3 ? [{ ic: "ios_share", t: "Tocá Compartir", k: "c" }, { ic: "add_box", t: "Agregar a inicio", k: "c" }, { ic: "notifications", t: "Permití los avisos", k: "c" }]
    : AYUDA.map(a => ({ ic: a.ic, t: a.t, k: "a" }));

  const puntos = (
    <div style={{ display: "flex", gap: 6, flex: movil ? "none" : 1, justifyContent: movil ? "center" : "flex-start" }} aria-hidden="true">
      {[0, 1, 2, 3, 4].map(i => <span key={i} style={{ height: 8, borderRadius: 4, width: i === paso ? 28 : 8, background: i === paso ? "#111318" : "#CDD0D7", transition: "width .2s" }} />)}
    </div>
  );

  const alToque = { onTouchStart: (e) => { toqueRef.current = e.touches[0].clientX; }, onTouchEnd: (e) => {
    const x0 = toqueRef.current; toqueRef.current = null;
    if (x0 == null) return;
    const dx = e.changedTouches[0].clientX - x0;
    if (dx < -60) siguiente(); else if (dx > 60) atras();
  } };

  const fondo = { position: "fixed", inset: 0, zIndex: 9995, background: "rgba(11,13,16,0.55)" };

  if (movil) {
    return (
      <div style={fondo}>
        <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="bienvenida-titulo" tabIndex={-1} {...alToque}
          style={{ position: "absolute", left: 0, right: 0, bottom: 0, top: 90, background: "#FFFFFF", borderRadius: "30px 30px 0 0", padding: "16px 22px calc(26px + env(safe-area-inset-bottom))", display: "flex", flexDirection: "column", gap: 14, outline: "none", overflowY: "auto" }}>
          <span style={{ width: 44, height: 5, borderRadius: 3, background: "#CDD0D7", alignSelf: "center", flexShrink: 0 }} />
          <div style={{ display: "flex", alignItems: "center" }}>
            <span style={{ fontSize: 14, fontWeight: 800, color: "#0E7490", flex: 1 }}>Paso {paso + 1} de 5</span>
            <button type="button" onClick={() => cerrar("saltar")} style={{ border: 0, background: "transparent", fontSize: 14, fontWeight: 700, color: "#6B6F7A", cursor: "pointer", minHeight: 44, padding: "0 4px" }}>Saltar</button>
          </div>
          <h2 id="bienvenida-titulo" style={{ margin: 0, fontSize: 28, lineHeight: 1.1, fontWeight: 900, letterSpacing: "-0.02em" }}>{P.tm}</h2>
          <p style={{ margin: 0, fontSize: 15, lineHeight: 1.5, color: "#3B3E46" }}>{P.dm}</p>
          {paso === 1 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <BloqueFoto perfil={perfil} nombre={miembro.nombre} tam={112} />
              <CamposPerfil perfil={perfil} />
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {movilItems.map(m => (
              <div key={m.t} style={{ background: "#F2F3F6", borderRadius: 18, padding: 14, display: "flex", alignItems: "center", gap: 12, minHeight: 44 }}>
                <span style={tile(m.k, 40)}><Icon name={m.ic} className="text-[22px]" /></span>
                <span style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.3 }}>{m.t}</span>
              </div>
            ))}
          </div>
          <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 12 }}>
            {puntos}
            <div style={{ display: "flex", gap: 10 }}>
              {paso > 0 && <button type="button" onClick={atras} style={{ height: 54, padding: "0 18px", borderRadius: 16, border: 0, background: "#F2F3F6", fontSize: 16, fontWeight: 800, cursor: "pointer" }}>Atrás</button>}
              <button type="button" onClick={siguiente} disabled={paso === 1 && (!perfil.completo || perfil.guardando)} style={{ flex: 1, height: 54, borderRadius: 16, border: 0, background: "#111318", color: "#FFFFFF", fontSize: 16, fontWeight: 800, cursor: "pointer", opacity: paso === 1 && !perfil.completo ? 0.4 : 1 }}>{perfil.guardando && paso === 1 ? "Guardando…" : ctaMovil}</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const oscuro = paso === 0;
  return (
    <div style={{ ...fondo, display: "grid", placeItems: "center", padding: 16 }}>
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="bienvenida-titulo" tabIndex={-1}
        style={{ width: "min(960px, 100%)", height: "min(620px, calc(100vh - 32px))", background: "#FFFFFF", borderRadius: 36, boxShadow: "0 50px 120px rgba(0,0,0,0.4)", display: "grid", gridTemplateColumns: "420px 1fr", overflow: "hidden", outline: "none" }}>
        <div style={{ padding: "44px 40px", display: "flex", flexDirection: "column", justifyContent: "center", background: oscuro ? "#0B0D10" : "#F2F3F6", color: oscuro ? "#FFFFFF" : "#111318" }}>
          {paso === 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 26, alignItems: "flex-start" }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2,72px)", gap: 16 }}>
                <span style={{ width: 72, height: 72, borderRadius: 22, background: "#22D3EE", boxShadow: "0 0 60px rgba(34,211,238,0.6)" }} />
                <span style={{ width: 72, height: 72, borderRadius: 22, background: "#10B981" }} />
                <span style={{ width: 72, height: 72, borderRadius: 22, background: "#8B5CF6" }} />
                <span style={{ width: 72, height: 72, borderRadius: 22, background: "#F59E0B" }} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <span style={{ fontSize: 20, fontWeight: 700, color: "#7D828E", textDecoration: "line-through" }}>The Crew OS</span>
                <span style={{ fontSize: 54, lineHeight: 0.9, fontWeight: 900, letterSpacing: "-0.04em" }}>SOA <span style={{ color: "#22D3EE" }}>10 PRO</span></span>
              </div>
            </div>
          )}
          {paso === 1 && <BloqueFoto perfil={perfil} nombre={miembro.nombre} />}
          {paso === 2 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12, width: "100%" }}>
              <span style={{ fontSize: 15, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: "#155E75" }}>Para {R.n}</span>
              {R.f.map(x => (
                <div key={x.t} style={{ background: "#FFFFFF", borderRadius: 20, padding: "16px 18px", display: "flex", alignItems: "center", gap: 14, boxShadow: "0 10px 24px rgba(17,19,24,0.08)" }}>
                  <span style={tile(x.k)}><Icon name={x.ic} className="text-[24px]" /></span>
                  <span style={{ fontSize: 17, fontWeight: 800, lineHeight: 1.2 }}>{x.t}</span>
                </div>
              ))}
            </div>
          )}
          {paso === 3 && (
            <div style={{ width: 220, height: 440, borderRadius: 38, background: "#111318", padding: 8, boxSizing: "border-box", alignSelf: "center" }}>
              <div style={{ width: "100%", height: "100%", borderRadius: 31, background: "#F2F3F6", padding: "34px 12px 12px", boxSizing: "border-box", display: "flex", flexDirection: "column", gap: 8 }}>
                <span style={{ fontSize: 18, fontWeight: 800, padding: "0 4px", color: "#111318" }}>Mi Día</span>
                <span style={{ height: 64, borderRadius: 14, background: "#FFFFFF" }} /><span style={{ height: 44, borderRadius: 14, background: "#FFFFFF" }} /><span style={{ height: 44, borderRadius: 14, background: "#FFFFFF" }} />
                <div style={{ marginTop: "auto", height: 48, borderRadius: 14, background: "#FFFFFF", display: "grid", gridTemplateColumns: "repeat(5,1fr)", alignItems: "center", justifyItems: "center", color: "#6B6F7A" }}>
                  <Icon name="home" className="text-[18px] text-[#0E7490]" /><Icon name="inbox" className="text-[18px]" /><Icon name="add_circle" className="text-[22px]" /><Icon name="calendar_month" className="text-[18px]" /><Icon name="person" className="text-[18px]" />
                </div>
              </div>
            </div>
          )}
          {paso === 4 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 22, alignItems: "flex-start" }}>
              <span style={{ width: 96, height: 96, borderRadius: 30, background: "#10B981", color: "#FFFFFF", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="check" className="text-[56px]" /></span>
              <span style={{ fontSize: 40, lineHeight: 1, fontWeight: 900, letterSpacing: "-0.03em" }}>Todo listo</span>
            </div>
          )}
        </div>

        <div style={{ padding: "40px 44px 32px", display: "flex", flexDirection: "column", gap: 18, minHeight: 0, overflowY: "auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 15, fontWeight: 800, color: "#0E7490", flex: 1 }}>Paso {paso + 1} de 5</span>
            <button type="button" onClick={() => cerrar("saltar")} style={{ border: 0, background: "transparent", fontSize: 15, fontWeight: 700, color: "#6B6F7A", cursor: "pointer" }}>Saltar</button>
          </div>
          <h2 id="bienvenida-titulo" style={{ margin: 0, fontSize: 38, lineHeight: 1.08, fontWeight: 900, letterSpacing: "-0.02em" }}>{P.t}</h2>
          <p style={{ margin: 0, fontSize: 18, lineHeight: 1.55, color: "#3B3E46" }}>{P.d}</p>
          {paso === 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {SIGUE_IGUAL.map(t => (
                <div key={t} style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 16, fontWeight: 700 }}>
                  <span style={{ color: "#10B981", display: "flex" }}><Icon name="check_circle" className="text-[22px]" /></span>{t}
                </div>
              ))}
            </div>
          )}
          {paso === 1 && <CamposPerfil perfil={perfil} />}
          {paso === 2 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {R.f.map(x => (
                <div key={x.t} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <span style={{ fontSize: 16, fontWeight: 800 }}>{x.t}</span>
                  <span style={{ fontSize: 15, lineHeight: 1.45, color: "#3B3E46" }}>{x.d}</span>
                </div>
              ))}
            </div>
          )}
          {paso === 3 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {appPasos.map((t, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 16, lineHeight: 1.4 }}>
                  <span style={{ width: 28, height: 28, borderRadius: 9, background: "#F2F3F6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 800, flexShrink: 0 }}>{i + 1}</span>{t}
                </div>
              ))}
            </div>
          )}
          {paso === 4 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button type="button" onClick={() => cerrar("cta", "/presentacion")} style={{ alignSelf: "flex-start", border: 0, background: "#EDE9FE", color: "#5B21B6", borderRadius: 14, padding: "10px 16px", fontSize: 15, fontWeight: 800, cursor: "pointer" }}>Ver la presentación del sistema →</button>
              {AYUDA.map(a => (
                <div key={a.t} style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 16, lineHeight: 1.4 }}>
                  <span style={{ color: "#0E7490", display: "flex" }}><Icon name={a.ic} className="text-[22px]" /></span>{a.t}
                </div>
              ))}
            </div>
          )}
          <div style={{ marginTop: "auto", display: "flex", alignItems: "center", gap: 12 }}>
            {puntos}
            <button type="button" onClick={atras} style={{ height: 52, padding: "0 18px", borderRadius: 16, border: 0, background: "#F2F3F6", fontSize: 16, fontWeight: 800, cursor: "pointer", color: "#111318", visibility: paso === 0 ? "hidden" : "visible" }}>Atrás</button>
            <button type="button" onClick={siguiente} disabled={paso === 1 && (!perfil.completo || perfil.guardando)} style={{ height: 52, padding: "0 24px", borderRadius: 16, border: 0, background: "#111318", color: "#FFFFFF", fontSize: 16, fontWeight: 800, display: "flex", alignItems: "center", gap: 8, cursor: "pointer", opacity: paso === 1 && !perfil.completo ? 0.4 : 1 }}>
              {perfil.guardando && paso === 1 ? "Guardando…" : ctaTxt}<Icon name="arrow_forward" className="text-[20px]" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
